import { supabase } from './supabase';
import { DUREES_CDD, DUREES_STAGE_MOIS, RYTHMES } from '../config/offres';
import { ProfilTropEloigneError, QuotaError, type QuotaRefusal } from './claude';
import type { Plan } from '../types';
import type {
  ContratCode,
  CorrectionPoint,
  DureeCdd,
  CreditsOffres,
  Decision,
  DemandeCorrection,
  DossierComplet,
  DossierFinal,
  EtapeAnalyse,
  EtapeCv,
  EtapeLettre,
  Offre,
  PreferencesOffres,
  ResultatRecherche,
  Rythme,
} from '../types/offres';

/**
 * Offres, lot 1 — tables `offres_preferences` et `offres_decisions` (RLS : ses lignes seulement),
 * edge function `offres-recherche` (recherche, revalidation des offres gardées).
 *
 * Les erreurs remontent sous un CODE (`offres_…`), jamais avec le message d'un prestataire : c'est
 * l'écran qui choisit le texte à afficher.
 */

async function utilisateurId(): Promise<string> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('offres_session');
  return user.id;
}

const CONTRATS: ContratCode[] = ['alternance', 'stage', 'cdi', 'cdd'];

function depuisLigne(l: Record<string, unknown>): PreferencesOffres {
  return {
    metier: typeof l.metier === 'string' ? l.metier : '',
    contrats: Array.isArray(l.contrats) ? l.contrats.filter((c): c is ContratCode => CONTRATS.includes(c as ContratCode)) : [],
    ville: typeof l.ville === 'string' ? l.ville : '',
    rayonKm: typeof l.rayon_km === 'number' ? l.rayon_km : 20,
    rythme: RYTHMES.includes(l.rythme as Rythme) ? (l.rythme as Rythme) : null,
    // Une valeur hors liste (ancienne ou illisible) est l'absence, jamais une exigence inventée.
    dureeStageMois: DUREES_STAGE_MOIS.includes(l.duree_stage_mois as number) ? (l.duree_stage_mois as number) : null,
    dureeCdd: DUREES_CDD.includes(l.duree_cdd as DureeCdd) ? (l.duree_cdd as DureeCdd) : null,
    permisB: typeof l.permis_b === 'boolean' ? l.permis_b : null,
    disponibleLe: typeof l.disponible_le === 'string' ? l.disponible_le : null,
  };
}

export async function lirePreferences(): Promise<PreferencesOffres | null> {
  const userId = await utilisateurId();
  const { data, error } = await supabase
    .from('offres_preferences')
    .select('metier, contrats, ville, rayon_km, rythme, duree_stage_mois, duree_cdd, permis_b, disponible_le')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw new Error('offres_preferences_lecture');
  return data ? depuisLigne(data as Record<string, unknown>) : null;
}

/**
 * Le serveur résout le métier (codes ROME) et la ville (commune) à la recherche suivante. Changer
 * l'un ou l'autre vide donc sa résolution : sans cela, un nouveau métier serait cherché avec les
 * codes ROME de l'ancien.
 */
export async function enregistrerPreferences(prefs: PreferencesOffres, precedentes: PreferencesOffres | null): Promise<void> {
  const userId = await utilisateurId();
  const metierChange = !precedentes || precedentes.metier.trim() !== prefs.metier.trim();
  const villeChange = !precedentes || precedentes.ville.trim() !== prefs.ville.trim();
  const { error } = await supabase.from('offres_preferences').upsert({
    user_id: userId,
    metier: prefs.metier.trim(),
    contrats: prefs.contrats,
    ville: prefs.ville.trim(),
    rayon_km: prefs.rayonKm,
    rythme: prefs.rythme,
    duree_stage_mois: prefs.dureeStageMois,
    duree_cdd: prefs.dureeCdd,
    permis_b: prefs.permisB,
    disponible_le: prefs.disponibleLe,
    updated_at: new Date().toISOString(),
    ...(metierChange ? { rome_codes: [] } : {}),
    ...(villeChange ? { code_insee: null, latitude: null, longitude: null } : {}),
  });
  if (error) throw new Error('offres_preferences_echec');
}

/** Métier de départ du formulaire. Un préremplissage manqué ne bloque rien : jamais d'erreur. */
export async function titreCvPrincipal(): Promise<string | null> {
  try {
    const userId = await utilisateurId();
    const { data, error } = await supabase
      .from('cvs')
      .select('title')
      .eq('user_id', userId)
      .eq('is_primary', true)
      .limit(1)
      .maybeSingle();
    if (error || !data) return null;
    const titre = (data as { title?: unknown }).title;
    return typeof titre === 'string' && titre.trim() ? titre.trim() : null;
  } catch {
    return null;
  }
}

const ETATS = ['sans_preferences', 'erreur', 'ok'];

export async function rechercher(): Promise<ResultatRecherche> {
  const { data, error } = await supabase.functions.invoke<ResultatRecherche>('offres-recherche', {
    body: { action: 'rechercher' },
  });
  if (error || !data || !ETATS.includes((data as { etat?: string }).etat ?? '')) throw new Error('offres_recherche_echec');
  return data;
}

/** Licence France Travail, art. 7 : purge des offres retirées. Ne bloque jamais l'affichage. */
export async function revaliderGardees(): Promise<void> {
  await supabase.functions.invoke('offres-recherche', { body: { action: 'revalider' } }).catch(() => undefined);
}

export async function decider(offre: Offre, decision: Decision): Promise<void> {
  const userId = await utilisateurId();
  const { error } = await supabase.from('offres_decisions').upsert({
    user_id: userId,
    offre_cle: offre.cle,
    decision,
    // Une offre « passée » n'est jamais relue ni réaffichée : la clé suffit à ne plus la proposer.
    // Y garder la copie complète (entreprise, contact) la conserverait sans fin et sans usage, ce que
    // la licence France Travail (art. 7) interdit pour une offre retirée de la source.
    apercu: decision === 'passee' ? {} : offre,
    updated_at: new Date().toISOString(),
  });
  if (error) throw new Error('offres_decision_echec');
}

export interface OffreGardee {
  decision: Decision;
  offre: Offre;
}

export async function listerGardees(): Promise<OffreGardee[]> {
  const userId = await utilisateurId();
  const { data, error } = await supabase
    .from('offres_decisions')
    .select('decision, apercu')
    .eq('user_id', userId)
    .in('decision', ['sauvegardee', 'interessee'])
    .order('updated_at', { ascending: false });
  if (error) throw new Error('offres_gardees_lecture');
  return ((data ?? []) as { decision: Decision; apercu: Offre }[]).map((l) => ({ decision: l.decision, offre: l.apercu }));
}

// ─────────────────────────── Dossier de candidature (lot 2) ───────────────────────────

const CODES_DOSSIER = [
  'cv_manquant',
  'offre_retiree',
  'offre_invalide',
  'dossier_introuvable',
  'etape_manquante',
  'correction_invalide',
  'ai_bad_output',
];

async function corpsErreur(error: unknown): Promise<Record<string, unknown> | null> {
  const context = (error as { context?: unknown }).context;
  if (!(context instanceof Response)) return null;
  try {
    return (await context.clone().json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Un refus de quota devient une `QuotaError` (le paywall s'ouvre sur la raison et le plan que le
 * serveur a appliqués) ; un refus connu, une erreur à code ; tout le reste, une erreur générique —
 * jamais le message d'un prestataire, qui pourrait porter une adresse ou un détail interne.
 */
async function appelDossier<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>('offres-dossier', { body });
  if (error) {
    const corps = await corpsErreur(error);
    if (corps?.error === 'quota_exceeded') {
      throw new QuotaError('offre_dossier', corps.reason as QuotaRefusal | undefined, corps.plan as Plan | undefined);
    }
    if (corps?.error === 'profil_trop_eloigne') throw new ProfilTropEloigneError(typeof corps.raison === 'string' && corps.raison ? corps.raison : null);
    if (typeof corps?.error === 'string' && CODES_DOSSIER.includes(corps.error)) throw new Error(`offres_dossier_${corps.error}`);
    throw new Error('offres_dossier_echec');
  }
  if (!data) throw new Error('offres_dossier_echec');
  return data;
}

export const analyserOffre = (offre: Offre) => appelDossier<EtapeAnalyse>({ action: 'analyser', offre });
export const adapterCvDossier = (dossierId: string) => appelDossier<EtapeCv>({ action: 'cv', dossierId });
export const redigerLettreDossier = (dossierId: string) => appelDossier<EtapeLettre>({ action: 'lettre', dossierId });
export const finaliserDossier = (dossierId: string) => appelDossier<DossierFinal>({ action: 'finaliser', dossierId });
export const corrigerPoint = (dossierId: string, demande: DemandeCorrection) =>
  appelDossier<{ corrections: Record<string, CorrectionPoint> }>({ action: 'corriger', dossierId, ...demande });
export const creditsOffres = () => appelDossier<CreditsOffres>({ action: 'credits' });

/**
 * Le dossier déjà préparé pour une offre, s'il existe : c'est ce qui permet de le rouvrir après avoir
 * fermé l'écran 5. Un échec de lecture rend null — le détail de l'offre s'affiche quand même.
 */
export async function dossierPourOffre(cle: string): Promise<{ id: string; statut: DossierComplet['statut'] } | null> {
  try {
    const userId = await utilisateurId();
    const { data, error } = await supabase.from('offres_dossiers').select('id, statut').eq('user_id', userId).eq('offre_cle', cle).maybeSingle();
    return error || !data ? null : (data as { id: string; statut: DossierComplet['statut'] });
  } catch {
    return null;
  }
}

/** Lecture directe : la politique RLS n'ouvre que la ligne de l'utilisateur, l'écriture reste au serveur. */
export async function lireDossier(dossierId: string): Promise<DossierComplet> {
  const userId = await utilisateurId();
  const { data, error } = await supabase
    .from('offres_dossiers')
    .select('id, statut, offre, cv_adapte, champs_modifies, lettre, reponses, controle, corrections')
    .eq('user_id', userId)
    .eq('id', dossierId)
    .maybeSingle();
  if (error || !data) throw new Error('offres_dossier_lecture');
  return data as DossierComplet;
}
